import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Image, Tag, Calendar, Clock, FileText, Bold, Italic, List, ListOrdered, Quote, Link as LinkIcon, Heading1, Heading2, Undo, Redo } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

const AdminBlogEditorPage = () => {
  const { postId } = useParams();
  const isEditing = Boolean(postId);
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('content');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Personal Injury',
    author: 'Amy C. Brown',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    readTime: '5 min read',
    image: '',
    imageAlt: '',
    featured: false,
    published: false
  });

  const categories = ['Personal Injury', 'Maritime Law', 'Car Accidents', 'First Responders', 'Slip and Falls'];

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExtension.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Start writing your blog post...',
      }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
  });

  // Update editor content when formData.content changes from fetch
  useEffect(() => {
    if (editor && formData.content && editor.getHTML() !== formData.content) {
      editor.commands.setContent(formData.content);
    }
  }, [editor, formData.content]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }

    if (isEditing) {
      fetchPost();
    }
  }, [token, isEditing, postId, navigate]);

  const fetchPost = async () => {
    try {
      // First get all posts to find by ID
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts?published_only=false`);
      const data = await response.json();
      const post = data.posts.find(p => p.id === postId);
      
      if (post) {
        setFormData(post);
      } else {
        setError('Post not found');
      }
    } catch (err) {
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: isEditing ? prev.slug : generateSlug(title)
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const url = isEditing 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${postId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        navigate('/admin/blog');
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save post');
      }
    } catch (err) {
      setError('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-blog-editor">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/blog" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-secondary">
              {isEditing ? 'Edit Post' : 'New Post'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {formData.slug && (
              <Link
                to={`/blog/${formData.slug}`}
                target="_blank"
                className="flex items-center gap-2 text-slate-500 hover:text-slate-700 px-3 py-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </Link>
            )}
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-hover transition-colors disabled:opacity-50"
              data-testid="save-post-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleTitleChange}
                placeholder="Post title..."
                className="w-full text-2xl font-heading font-bold text-secondary border-0 focus:ring-0 outline-none placeholder:text-slate-300"
                data-testid="post-title"
                required
              />
              <div className="mt-2 text-sm text-slate-500">
                Slug: <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{formData.slug || 'auto-generated'}</span>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="border-b border-slate-200 flex">
                <button
                  type="button"
                  onClick={() => setActiveTab('content')}
                  className={`px-6 py-3 font-medium transition-colors ${
                    activeTab === 'content' 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-2" />
                  Content
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('excerpt')}
                  className={`px-6 py-3 font-medium transition-colors ${
                    activeTab === 'excerpt' 
                      ? 'text-primary border-b-2 border-primary' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Excerpt
                </button>
              </div>

              <div className="p-6">
                {activeTab === 'content' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Post Content
                    </label>
                    {/* TipTap Toolbar */}
                    <div className="border border-slate-200 rounded-t-lg bg-slate-50 p-2 flex flex-wrap gap-1">
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={`p-2 rounded hover:bg-slate-200 ${editor?.isActive('heading', { level: 2 }) ? 'bg-slate-200 text-primary' : 'text-slate-600'}`}
                        title="Heading 2"
                      >
                        <Heading2 className="w-4 h-4" />
                      </button>
                      <div className="w-px bg-slate-300 mx-1" />
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().toggleBold().run()}
                        className={`p-2 rounded hover:bg-slate-200 ${editor?.isActive('bold') ? 'bg-slate-200 text-primary' : 'text-slate-600'}`}
                        title="Bold"
                      >
                        <Bold className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().toggleItalic().run()}
                        className={`p-2 rounded hover:bg-slate-200 ${editor?.isActive('italic') ? 'bg-slate-200 text-primary' : 'text-slate-600'}`}
                        title="Italic"
                      >
                        <Italic className="w-4 h-4" />
                      </button>
                      <div className="w-px bg-slate-300 mx-1" />
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().toggleBulletList().run()}
                        className={`p-2 rounded hover:bg-slate-200 ${editor?.isActive('bulletList') ? 'bg-slate-200 text-primary' : 'text-slate-600'}`}
                        title="Bullet List"
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                        className={`p-2 rounded hover:bg-slate-200 ${editor?.isActive('orderedList') ? 'bg-slate-200 text-primary' : 'text-slate-600'}`}
                        title="Numbered List"
                      >
                        <ListOrdered className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                        className={`p-2 rounded hover:bg-slate-200 ${editor?.isActive('blockquote') ? 'bg-slate-200 text-primary' : 'text-slate-600'}`}
                        title="Quote"
                      >
                        <Quote className="w-4 h-4" />
                      </button>
                      <div className="w-px bg-slate-300 mx-1" />
                      <button
                        type="button"
                        onClick={setLink}
                        className={`p-2 rounded hover:bg-slate-200 ${editor?.isActive('link') ? 'bg-slate-200 text-primary' : 'text-slate-600'}`}
                        title="Add Link"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                      <div className="w-px bg-slate-300 mx-1" />
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().undo().run()}
                        disabled={!editor?.can().undo()}
                        className="p-2 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-30"
                        title="Undo"
                      >
                        <Undo className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => editor?.chain().focus().redo().run()}
                        disabled={!editor?.can().redo()}
                        className="p-2 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-30"
                        title="Redo"
                      >
                        <Redo className="w-4 h-4" />
                      </button>
                    </div>
                    {/* TipTap Editor */}
                    <div className="border border-t-0 border-slate-200 rounded-b-lg">
                      <EditorContent 
                        editor={editor} 
                        className="prose-editor min-h-[350px] p-4 prose prose-sm max-w-none focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'excerpt' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Excerpt (shown in blog list)
                    </label>
                    <textarea
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      placeholder="A brief summary of the post..."
                      data-testid="post-excerpt"
                      required
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-secondary mb-4">Publish Settings</h3>
              
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="published"
                    checked={formData.published}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary rounded focus:ring-primary"
                  />
                  <span className="text-slate-700">Published</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary rounded focus:ring-primary"
                  />
                  <span className="text-slate-700">Featured post</span>
                </label>
              </div>
            </div>

            {/* Category */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-secondary mb-4 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Category
              </h3>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                data-testid="post-category"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Meta Info */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-secondary mb-4">Meta Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Date
                  </label>
                  <input
                    type="text"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-sm"
                    placeholder="February 17, 2026"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Read Time
                  </label>
                  <input
                    type="text"
                    name="readTime"
                    value={formData.readTime}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-sm"
                    placeholder="5 min read"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Author
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-secondary mb-4 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Featured Image
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Image URL
                  </label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-sm"
                    placeholder="https://..."
                    data-testid="post-image"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    name="imageAlt"
                    value={formData.imageAlt}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-sm"
                    placeholder="Describe the image..."
                    required
                  />
                </div>

                {formData.image && (
                  <div className="mt-4">
                    <img
                      src={formData.image}
                      alt={formData.imageAlt || 'Preview'}
                      className="w-full h-40 object-cover rounded-lg"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Slug (Advanced) */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-secondary mb-4">URL Slug</h3>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-sm font-mono"
                placeholder="post-url-slug"
                data-testid="post-slug"
                required
              />
              <p className="mt-2 text-xs text-slate-500">
                URL: /blog/{formData.slug}
              </p>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminBlogEditorPage;
